/**
 * Pure keyboard-to-drive-state helpers.
 *
 * These are 100-ton trucks: inputs are deliberate, slew-limited nudges, and
 * axes **hold** their position when the keys are released - steering does not
 * snap back to center and the throttle does not drop to zero. The only
 * instant transition is e-stop (handled elsewhere), which latches into REST.
 *
 * @module
 */

import type { DriveState } from "./schemas.ts";

/** The truck's safe resting state. */
export const REST: DriveState = { steering: 0, throttle: 0, brake: 1, gear: "neutral" };

/** Slew rates in normalized units per second. Tuned for haul-truck patience. */
export const RATES = {
	/** Steering: full lock takes ~3 s of held key. */
	steer: 0.35,
	/** Recentering (C) is a touch quicker than turning further. */
	steerCenter: 0.5,
	/** Throttle builds slowly... */
	throttleUp: 0.4,
	/** ...and backs off faster than it builds. */
	throttleDown: 0.8,
	/** Brakes apply quickly (safety), */
	brakeUp: 0.9,
	/** but release gently so the truck doesn't lurch. */
	brakeDown: 0.45,
};

/** Move `current` toward `target` by at most `step`, without overshooting. */
export function moveToward(current: number, target: number, step: number): number {
	if (Math.abs(target - current) <= step) return target;
	return current + Math.sign(target - current) * step;
}

/**
 * Integrate the held keys into the next analog state over `dt` seconds.
 *
 * - `A`/`D` (or arrows) slew the steering toward full lock; releasing holds
 *   the current angle, and `C` slews it back to center.
 * - `W` first eases the brake off, then builds throttle.
 * - `S` first eases the throttle off, then applies brake (so it always wins
 *   over `W`).
 * - No keys: everything holds where it is.
 */
export function applyKeys(
	current: DriveState,
	held: ReadonlySet<string>,
	dt: number,
): Pick<DriveState, "steering" | "throttle" | "brake"> {
	let { steering, throttle, brake } = current;

	const left = held.has("a") || held.has("arrowleft");
	const right = held.has("d") || held.has("arrowright");
	const accelerating = held.has("w") || held.has("arrowup");
	const braking = held.has("s") || held.has("arrowdown");

	if (left !== right) {
		const dir = right ? 1 : -1;
		steering = Math.max(-1, Math.min(1, steering + dir * RATES.steer * dt));
	} else if (held.has("c")) {
		steering = moveToward(steering, 0, RATES.steerCenter * dt);
	}

	if (braking) {
		// Ease off the throttle before touching the brakes.
		if (throttle > 0) {
			throttle = moveToward(throttle, 0, RATES.throttleDown * dt);
		} else {
			brake = moveToward(brake, 1, RATES.brakeUp * dt);
		}
	} else if (accelerating) {
		// Release the brake before applying power.
		if (brake > 0) {
			brake = moveToward(brake, 0, RATES.brakeDown * dt);
		} else {
			throttle = moveToward(throttle, 1, RATES.throttleUp * dt);
		}
	}

	return { steering, throttle, brake };
}

/** Return whether an analog state differs enough to publish. */
export function changed(current: DriveState, next: Pick<DriveState, "steering" | "throttle" | "brake">): boolean {
	const epsilon = 0.01;
	return (
		Math.abs(current.steering - next.steering) > epsilon ||
		Math.abs(current.throttle - next.throttle) > epsilon ||
		Math.abs(current.brake - next.brake) > epsilon
	);
}
